import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiSearch, FiX, FiChevronDown } from "react-icons/fi";
import { trackAnimeSearch } from "../../utils/analytics";
import SEOHead from "../SEO/SEOHead";
import AnimeCard from "./Pages/AnimeCard";
import "./AnimeHubHome.css";
import "./Pages/AnimeStyle.css";

const GENRES = [
  { id: 1, name: "Action" }, { id: 2, name: "Adventure" }, { id: 4, name: "Comedy" },
  { id: 8, name: "Drama" }, { id: 10, name: "Fantasy" }, { id: 14, name: "Horror" },
  { id: 7, name: "Mystery" }, { id: 22, name: "Romance" }, { id: 24, name: "Sci-Fi" },
  { id: 36, name: "Slice of Life" }, { id: 30, name: "Sports" }, { id: 37, name: "Supernatural" },
  { id: 41, name: "Thriller" }, { id: 25, name: "Shoujo" }, { id: 27, name: "Shounen" },
  { id: 46, name: "Award Winning" },
];

const SEASONS = [
  { value: "winter", label: "Winter", dates: "Jan – Mar" },
  { value: "spring", label: "Spring", dates: "Apr – Jun" },
  { value: "summer", label: "Summer", dates: "Jul – Sep" },
  { value: "fall", label: "Fall", dates: "Oct – Dec" },
];

const YEARS = [];
for (let y = 2026; y >= 2000; y--) YEARS.push(y);

const getCurrentSeason = () => {
  const m = new Date().getMonth() + 1;
  if (m <= 3) return "winter";
  if (m <= 6) return "spring";
  if (m <= 9) return "summer";
  return "fall";
};

const AnimeHubHome = () => {
  const [animeList, setAnimeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedSeason, setSelectedSeason] = useState(getCurrentSeason());
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [sortBy, setSortBy] = useState("score");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const debounceRef = useRef(null);

  const fetchAnime = useCallback(async () => {
    setLoading(true);
    try {
      let allAnime = [];
      const seen = new Set();

      if (selectedGenre) {
        const base = `https://api.jikan.moe/v4/anime?genres=${selectedGenre}&order_by=${sortBy}&sort=desc&limit=25&min_score=1&start_date=${selectedYear}-01-01&end_date=${selectedYear}-12-31`;
        const res = await fetch(base + "&page=1");
        const data = await res.json();
        (data.data || []).forEach(a => { if (!seen.has(a.mal_id)) { seen.add(a.mal_id); allAnime.push(a); } });
        await new Promise(r => setTimeout(r, 400));
        const res2 = await fetch(base + "&page=2");
        const data2 = await res2.json();
        (data2.data || []).forEach(a => { if (!seen.has(a.mal_id)) { seen.add(a.mal_id); allAnime.push(a); } });
      } else {
        for (let page = 1; page <= 3; page++) {
          if (page > 1) await new Promise(r => setTimeout(r, 400));
          const res = await fetch(`https://api.jikan.moe/v4/seasons/${selectedYear}/${selectedSeason}?page=${page}&limit=25`);
          const data = await res.json();
          const items = data.data || [];
          if (items.length === 0) break;
          items.forEach(a => { if (!seen.has(a.mal_id)) { seen.add(a.mal_id); allAnime.push(a); } });
        }
        if (sortBy === "score") allAnime.sort((a, b) => (b.score || 0) - (a.score || 0));
        else if (sortBy === "members") allAnime.sort((a, b) => (b.members || 0) - (a.members || 0));
        else if (sortBy === "start_date") allAnime.sort((a, b) => new Date(b.aired?.from || 0) - new Date(a.aired?.from || 0));
        else if (sortBy === "episodes") allAnime.sort((a, b) => (b.episodes || 0) - (a.episodes || 0));
      }
      setAnimeList(allAnime);
    } catch (error) {
      console.error("Error fetching anime:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedSeason, selectedGenre, sortBy]);

  const searchAnime = useCallback(async (query) => {
    if (!query.trim()) { setIsSearchMode(false); return; }
    setLoading(true);
    setIsSearchMode(true);
    try {
      const res = await fetch(`https://api.jikan.moe/v4/anime?q=${encodeURIComponent(query)}&limit=25&order_by=score&sort=desc`);
      const data = await res.json();
      setAnimeList(data.data || []);
      trackAnimeSearch(query, data.data?.length || 0);
    } catch (error) { console.error("Search error:", error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!isSearchMode) fetchAnime(); }, [fetchAnime, isSearchMode]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) { setIsSearchMode(false); return; }
    debounceRef.current = setTimeout(() => searchAnime(value), 500);
  };

  const clearSearch = () => { setSearchQuery(""); setIsSearchMode(false); };

  const handleGenreClick = (genreId) => {
    setSelectedGenre(selectedGenre === genreId ? null : genreId);
    setIsSearchMode(false);
    setSearchQuery("");
  };

  const seasonLabel = SEASONS.find(s => s.value === selectedSeason);
  const genreLabel = selectedGenre ? GENRES.find(g => g.id === selectedGenre)?.name : null;
  const resultTitle = isSearchMode
    ? `Results for "${searchQuery}"`
    : genreLabel
    ? `${genreLabel} — ${selectedYear}`
    : `${seasonLabel?.label} ${selectedYear}`;

  return (
    <div className="animeHubHome">
      <SEOHead
        title="Anime Hub - Browse by Season, Genre & Rating | ChibiDash"
        description="Explore anime by season, year, and genre on ChibiDash."
        path="/animehub"
      />

      {/* Search */}
      <div className="hub-search">
        <FiSearch className="hub-search-icon" />
        <input
          type="text"
          placeholder="Search anime..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="hub-search-input"
        />
        {searchQuery && (
          <button className="hub-search-clear" onClick={clearSearch}><FiX /></button>
        )}
      </div>

      {/* Filters */}
      {!isSearchMode && (
        <div className="hub-filters">
          <div className="hub-filters-top">
            <div className="hub-chip-group">
              {SEASONS.map(s => (
                <button
                  key={s.value}
                  className={`hub-chip ${selectedSeason === s.value && !selectedGenre ? "active" : ""}`}
                  onClick={() => { setSelectedSeason(s.value); setSelectedGenre(null); }}
                >
                  {s.label}<span className="hub-chip-sub">{s.dates}</span>
                </button>
              ))}
            </div>
            <div className="hub-dropdowns">
              <select className="hub-select" value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="hub-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="score">Top Rated</option>
                <option value="members">Most Popular</option>
                <option value="start_date">Newest</option>
                <option value="episodes">Most Episodes</option>
              </select>
            </div>
          </div>
          <div className="hub-genres">
            {GENRES.map(g => (
              <button
                key={g.id}
                className={`hub-genre ${selectedGenre === g.id ? "active" : ""}`}
                onClick={() => handleGenreClick(g.id)}
              >
                {g.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="hub-results">
        <div className="hub-results-header">
          <h2 className="hub-results-title">{resultTitle}</h2>
          <span className="hub-results-count">{animeList.length} anime</span>
        </div>

        {loading ? (
          <div className="loading-spinner-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading...</p>
          </div>
        ) : animeList.length > 0 ? (
          <div className="small-anime-grid">
            {animeList.map(anime => <AnimeCard key={anime.mal_id} anime={anime} />)}
          </div>
        ) : (
          <p className="empty-text">No anime found. Try different filters.</p>
        )}
      </div>
    </div>
  );
};

export default AnimeHubHome;
